import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { logSafeAxiosError } from 'src/api/axiosClient/errorHandling';
import { getCurrentCompanyId } from 'src/modules/companies/slices';
import { RootState } from 'src/store';
import { debounce } from 'ts-debounce';
import { useAssetService, useAssetLibraryService } from '../services';
import { MediaInfo } from '../types';

export const useAssetLibraryViewController = () => {
  const currentCompanyId = useSelector(getCurrentCompanyId) as string;

  const {
    loading: assetLibraryLoading,
    filter: assetLibraryFilter,
    list: assetList,
    getAssets,
    loadMoreAssets,
    changeAssetLibraryFilter,
    resetAssetLibrary,
    updateAssetList,
  } = useAssetLibraryService();
  
  const { updateAsset, deleteAsset, createAndUploadAsset } = useAssetService();

  const initLoad = useCallback(async () => {
    try {
      await getAssets(currentCompanyId);
    } catch (e) {
      logSafeAxiosError(e);
    }
  }, [currentCompanyId, getAssets]);

  const reloadAssets = useCallback(async () => {
    try {
      await getAssets(currentCompanyId);
    } catch (e) {
      logSafeAxiosError(e);
    }
  }, [currentCompanyId, getAssets]);

  const reloadAssetsDebounce = useMemo(() => debounce(reloadAssets, 500), [reloadAssets]);

  const changeAssetFilterForm = useCallback(async (filter: Partial<RootState["assetLibrary"]["filter"]>) => {
    changeAssetLibraryFilter(filter);
    await reloadAssets();
  }, [changeAssetLibraryFilter, reloadAssets]);

  const changeAssetSearchFilter = useCallback(async (query: string) => {
    changeAssetLibraryFilter({ query });
    await reloadAssetsDebounce();
  }, [changeAssetLibraryFilter, reloadAssetsDebounce]);

  const changeMimeTypeFilter = useCallback(async (filterValue: string) => {
    
    changeAssetLibraryFilter({ mime_type: filterValue === '' ? undefined : filterValue });
    await reloadAssets();
  }, [changeAssetLibraryFilter, reloadAssets]);

  const removeAsset = useCallback(async (assetId: string) => {
    try {
      await deleteAsset(currentCompanyId, assetId);
      await getAssets(currentCompanyId);
    } catch (e) {
      logSafeAxiosError(e);
    }
  }, [currentCompanyId, deleteAsset, getAssets]);

  const uploadAsset = useCallback(async (file: File, name: string): Promise<MediaInfo> => {
    return createAndUploadAsset(currentCompanyId, file, name);
  }, [currentCompanyId, createAndUploadAsset]);

  const updateAssetName = useCallback(async (assetId: string, name: string) => {
    await updateAsset(currentCompanyId, assetId, name);
  }, [currentCompanyId, updateAsset]);

  useEffect(() => {
    initLoad();

    return () => {
      resetAssetLibrary();
    };
  }, [initLoad, resetAssetLibrary]);

  const loadMore = useCallback(async () => {
    try {
      await loadMoreAssets(currentCompanyId);
    } catch (e) {
      logSafeAxiosError(e);
    }
  }, [currentCompanyId, loadMoreAssets]);

  return {
    isPageLoading: assetLibraryLoading.getList,
    isLoadingMore: assetLibraryLoading.loadMore,
    isUploading: assetLibraryLoading.upload,
    isUpdating: assetLibraryLoading.update,
    isDeleting: assetLibraryLoading.delete,
    assetLibraryFilter,
    assetList,
    changeAssetFilterForm,
    changeAssetSearchFilter,
    changeMimeTypeFilter,
    reloadAssets,
    removeAsset,
    uploadAsset,
    updateAssetName,
    updateAssetList,
    loadMore,
  };
}; 